import type { KeyboardEvent, ReactNode, Ref } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from './cn';
import {
  directoryListKeydown,
  directoryListShellClassName,
  directoryOptionChevronClassName,
  directoryOptionClassName,
} from './directoryListRow';

/**
 * Generic single-select listbox for master–detail directories (PRO-0320, ADR-015 /
 * DS-6 / NF-10). Renders a `role="listbox"` of `role="option"` rows with a roving
 * tabindex (one tab stop), Arrow/Home/End navigation (no wrap), and Enter/Space
 * activation — the honest single-select replacement for tokenised selection `<table>`s.
 *
 * The primitive owns presentation + keyboard only; the host owns data, selection
 * state, `localStorage` memory, and skeleton/empty/error states. Token-only styling.
 */

export interface DirectoryRowContent {
  /** Optional leading indicator (state/status dot, icon). Decorative → `aria-hidden`. */
  leading?: ReactNode;
  /** Primary identity line. */
  title: ReactNode;
  /** Secondary metadata line (already separated, e.g. "Broadcast • SMS + Email"). */
  meta?: ReactNode;
  /** Wrap the meta line instead of truncating it (PRO-1770 compose labels). */
  wrapMeta?: boolean;
  /** Trailing badges/labels (`Badge` nodes). Not part of the option's accessible name. */
  trailing?: ReactNode;
  /** Extra accessible text appended to the option name (e.g. "Requires L2 approval"). No PII. */
  accessibleSuffix?: string | undefined;
}

export interface DirectoryListProps<T> {
  /** Accessible name source — usually the id of the host's section heading. */
  ariaLabelledBy: string;
  items: readonly T[];
  /** Stable key + option-id source. */
  getKey: (item: T) => string;
  /** Currently selected key (controlled by host), or null. */
  selectedId: string | null;
  /** Fired on click or Enter/Space activation. */
  onSelect: (id: string) => void;
  /** Per-item content mapping. */
  renderRow: (item: T) => DirectoryRowContent;
  className?: string;
}

interface DirectoryListRowProps {
  /** Option element id (stable; not used for aria-activedescendant). */
  id: string;
  /** → `aria-selected`. */
  selected: boolean;
  /** Roving focus target: `tabIndex` 0 when active, -1 otherwise. */
  active: boolean;
  content: DirectoryRowContent;
  /** Click / Enter / Space. */
  onActivate: () => void;
  /** Sync the roving index when this row receives focus (Tab/click into the list). */
  onFocusActive: () => void;
  rowRef: Ref<HTMLLIElement>;
}

function DirectoryListRow({
  id,
  selected,
  active,
  content,
  onActivate,
  onFocusActive,
  rowRef,
}: DirectoryListRowProps) {
  const labelledBy = content.accessibleSuffix ? `${id}-title ${id}-suffix` : `${id}-title`;
  return (
    // role="option" with roving tabIndex: keyboard activation (Enter/Space) is handled
    // by the parent list's key handler over the roving-focus target, not per-<li> key
    // events. The onClick is the equivalent mouse affordance (NF-10 keyboard path is
    // roving focus + the list handler).
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <li
      ref={rowRef}
      id={id}
      role="option"
      aria-selected={selected}
      aria-labelledby={labelledBy}
      tabIndex={active ? 0 : -1}
      className={directoryOptionClassName(selected)}
      onClick={onActivate}
      onFocus={onFocusActive}
    >
      {content.leading ? (
        <span aria-hidden="true" className="flex shrink-0 items-center">
          {content.leading}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span id={`${id}-title`} className="block truncate text-base font-medium text-text-primary">
          {content.title}
        </span>
        {content.meta ? (
          <span
            className={cn(
              'block text-sm text-text-secondary',
              content.wrapMeta ? 'min-w-0 whitespace-normal break-words' : 'truncate',
            )}
          >
            {content.meta}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {content.trailing ? content.trailing : null}
        <ChevronRight
          aria-hidden
          focusable={false}
          className={directoryOptionChevronClassName(selected)}
        />
      </span>
      {content.accessibleSuffix ? (
        <span id={`${id}-suffix`} className="sr-only">
          {content.accessibleSuffix}
        </span>
      ) : null}
    </li>
  );
}

export function DirectoryList<T>({
  ariaLabelledBy,
  items,
  getKey,
  selectedId,
  onSelect,
  renderRow,
  className,
}: DirectoryListProps<T>) {
  const selectedIndex =
    selectedId === null ? -1 : items.findIndex((item) => getKey(item) === selectedId);
  const [activeIndex, setActiveIndex] = useState(() => (selectedIndex >= 0 ? selectedIndex : 0));
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Track the host's selection as the roving anchor when it changes externally.
  useEffect(() => {
    if (selectedIndex >= 0) {
      setActiveIndex(selectedIndex);
    }
  }, [selectedIndex]);

  // Keep the active index in range if the list shrinks.
  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(items.length - 1, 0)));
  }, [items.length]);

  const moveActive = (nextIndex: number) => {
    setActiveIndex(nextIndex);
    rowRefs.current[nextIndex]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const next = directoryListKeydown(event, {
      activeIndex,
      count: items.length,
      onActivate: () => {
        const item = items[activeIndex];
        if (item !== undefined) {
          onSelect(getKey(item));
        }
      },
    });
    if (next !== null && next !== activeIndex) {
      moveActive(next);
    }
  };

  return (
    <ul
      role="listbox"
      aria-labelledby={ariaLabelledBy}
      className={cn(directoryListShellClassName, className)}
      onKeyDown={handleKeyDown}
    >
      {items.map((item, index) => {
        const key = getKey(item);
        return (
          <DirectoryListRow
            key={key}
            rowRef={(el) => {
              rowRefs.current[index] = el;
            }}
            id={`${ariaLabelledBy}-option-${key}`}
            selected={key === selectedId}
            active={index === activeIndex}
            content={renderRow(item)}
            onActivate={() => onSelect(key)}
            onFocusActive={() => setActiveIndex(index)}
          />
        );
      })}
    </ul>
  );
}
